import { GameState } from "../core/game-state"

export interface PortalData {
  isPortal: boolean
  username: string
  color: string
  speed: number
  hp: number
  ref: string
  avatarUrl: string
  team: string
  rotation: { x: number; y: number; z: number }
  velocity: { x: number; y: number; z: number }
}

export class PortalManager {
  private static _instance: PortalManager
  private data: PortalData
  private originalParams: URLSearchParams

  private constructor() {
    this.originalParams = new URLSearchParams(window.location.search)
    this.data = this.parseUrlParams()
  }

  public static get instance(): PortalManager {
    if (!PortalManager._instance) {
      PortalManager._instance = new PortalManager()
    }
    return PortalManager._instance
  }

  private parseUrlParams(): PortalData {
    const params = new URLSearchParams(window.location.search)
    return {
      isPortal: params.get("portal") === "true",
      username: params.get("username") || "",
      color: params.get("color") || "",
      speed: Number.parseFloat(params.get("speed") || "0"),
      hp: Number.parseInt(params.get("hp") || "100", 10),
      ref: params.get("ref") || "",
      avatarUrl: params.get("avatar_url") || "",
      team: params.get("team") || "",
      rotation: {
        x: Number.parseFloat(params.get("rotation_x") || "0"),
        y: Number.parseFloat(params.get("rotation_y") || "0"),
        z: Number.parseFloat(params.get("rotation_z") || "0"),
      },
      velocity: {
        x: Number.parseFloat(params.get("speed_x") || "0"),
        y: Number.parseFloat(params.get("speed_y") || "0"),
        z: Number.parseFloat(params.get("speed_z") || "0"),
      },
    }
  }

  public getPortalData(): PortalData {
    return this.data
  }

  public getReturnPortalUrl(): string {
    if (!this.data.ref) return ""

    // We want to return to the original ref URL
    let returnUrlStr = this.data.ref

    // Check if it's already a full URL or needs prepending
    if (!returnUrlStr.startsWith("http")) {
      returnUrlStr = `https://${returnUrlStr}`
    }

    try {
      const url = new URL(returnUrlStr)

      // Merge original params (preserving what the previous game sent)
      this.originalParams.forEach((value, key) => {
        if (key !== "portal") {
          url.searchParams.set(key, value)
        }
      })

      // Update with current HP
      const state = GameState.instance
      if (state.player) {
        url.searchParams.set(
          "hp",
          Math.round((state.player.hp / state.player.maxHp) * 100).toString(),
        )
      }

      return url.toString()
    } catch (e) {
      // Fallback if URL is invalid
      return returnUrlStr
    }
  }

  public applyPortalDataToGameState(state: GameState): void {
    if (!this.data.isPortal) return

    if (this.data.username) {
      state.playerName = this.data.username
    }
    if (this.data.color) {
      state.playerColor = this.data.color
    }

    // Apply HP if it's a valid number
    if (this.data.hp > 0 && this.data.hp <= 100) {
      if (state.player) {
        state.player.hp = (this.data.hp / 100) * state.player.maxHp
      }
    }
  }

  public getExitPortalUrl(
    params: Partial<Record<string, string | number>> = {},
  ): string {
    const baseUrl = "https://vibej.am/portal/2026"
    const url = new URL(baseUrl)

    // Add current game state to params
    const state = GameState.instance
    const defaultParams: Record<string, string> = {
      username: state.playerName,
      color: state.playerColor,
      hp: state.player
        ? Math.round((state.player.hp / state.player.maxHp) * 100).toString()
        : "100",
      ref: window.location.origin + window.location.pathname,
      avatar_url: this.data.avatarUrl,
      team: this.data.team,
    }

    const finalParams = { ...defaultParams, ...params }

    for (const [key, value] of Object.entries(finalParams)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, value.toString())
      }
    }

    return url.toString()
  }
}
